import { IntlShape } from "react-intl";

import {
  TranslatableString,
  TranslationKey,
  ValidationErrorEntry,
  instanceOfTranslatableString,
} from "@nens/rana/api-v1";
import { listToString } from "@nens/shared-utils";

export type ErrorResponse = {
  response: Response;
};

export type Errors = {
  [key: string]: string | Errors;
};

export const isErrorResponse = (error: unknown): error is ErrorResponse => {
  return (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    error.response instanceof Response
  );
};

function getTranslatedMessage(
  item: TranslatableString,
  intl: IntlShape,
  context?: { filename?: string }
): string | null {
  const contextFromApi = item.ctx || {};

  switch (item.type) {
    case TranslationKey.FileTooLargeToProcess:
      return intl.formatMessage({
        id: "file_too_large_to_process",
        defaultMessage: "The file is too large for a preview (more than {max_size} GB).",
      });

    case TranslationKey.FileProcessUnknownError:
      return intl.formatMessage({
        id: "file_process_unknown_error",
        defaultMessage: "An unknown error occurred while processing the file.",
      });

    case TranslationKey.LizardScenarioProcessingError:
      return intl.formatMessage({
        id: "lizard_scenario_processing_error",
        defaultMessage:
          "An error occurred while processing the scenario (Lizard task id: {lizard_task_id}).",
      });

    case TranslationKey.FileFormatUnsupported:
      return intl.formatMessage({
        id: "file_format_unsupported",
        defaultMessage: "The file format is not supported.",
      });

    case TranslationKey.FileIncompleteOrCorrupt:
      return intl.formatMessage({
        id: "file_incomplete_or_corrupt",
        defaultMessage: "The file is corrupt or incomplete.",
      });

    case TranslationKey.FileHasInvalidCrs:
      return intl.formatMessage({
        id: "file_has_invalid_crs",
        defaultMessage: "The file has a coordinate system (CRS) that is not recognized.",
      });

    case TranslationKey.RasterHasNoBands:
      return intl.formatMessage({
        id: "raster_has_no_bands",
        defaultMessage: 'The raster file has no layers ("bands").',
      });

    case TranslationKey.VectorHasNoLayers:
      return intl.formatMessage({
        id: "vector_has_no_layers",
        defaultMessage: "The vector file has no layers.",
      });

    case TranslationKey.FileProcessMemoryConstraints:
      return intl.formatMessage({
        id: "file_process_memory_constraints",
        defaultMessage: "The file processing failed due to memory constraints.",
      });

    case TranslationKey.UnpackedFileTooLarge:
      return intl.formatMessage({
        id: "unpacked_file_too_large",
        defaultMessage: "An unpacked file is too large to process (more than {max_size} GB).",
      });

    case TranslationKey.PublicationVersionAlreadyExists:
      return intl.formatMessage({
        id: "publication_version_already_exists",
        defaultMessage: "A publication with this version already exists.",
      });

    case TranslationKey.FileOrDirectoryAlreadyExists:
      // Special handling: we have additional context from the frontend
      if (
        contextFromApi &&
        typeof contextFromApi === "object" &&
        "path" in contextFromApi &&
        typeof contextFromApi.path === "string"
      ) {
        if (context?.filename) {
          return intl.formatMessage(
            {
              id: "error.file_case_conflict",
              defaultMessage:
                'Cannot upload "{uploadedFile}" because a file with the same name but different capitalization ("{existingFile}") already exists.',
            },
            { uploadedFile: context.filename, existingFile: contextFromApi.path }
          );
        } else {
          return intl.formatMessage(
            {
              id: "file_or_directory_already_exists",
              defaultMessage:
                "A file with this name (but different capitalization) already exists: {path}",
            },
            { path: contextFromApi.path }
          );
        }
      }
      return null;

    case TranslationKey.ShapefileSidecarFilesMissing:
      return intl.formatMessage({
        id: "shapefile_sidecar_files_missing",
        defaultMessage: "Required shapefile sidecar files are missing.",
      });

    default:
      return null;
  }
}

export const getErrors = async (
  error: NonNullable<unknown>,
  intl: IntlShape,
  context?: { filename?: string }
): Promise<Errors> => {
  if (!isErrorResponse(error)) {
    if (error instanceof Error) {
      return { error: error.message };
    }
    if (typeof error === "string") {
      return { error };
    }
    return { error: "An unknown error occurred" };
  }

  let detail: string | (ValidationErrorEntry | TranslatableString)[] | undefined;
  let message: string | undefined;

  try {
    const parsed = await error.response.json();
    detail = parsed?.detail;
    message = parsed?.message;
  } catch {
    // Response is not valid JSON (e.g., a 500 error with an HTML/plain-text response)
    const { status, statusText } = error.response;
    return { error: `${status} ${statusText}`.trim() };
  }

  if (!detail) {
    if (message) return { error: message };
    return { error: "An error occurred" };
  }

  if (typeof detail === "string") {
    return { error: detail };
  }

  const errors: Errors = {};

  detail.forEach((item: ValidationErrorEntry | TranslatableString) => {
    const { msg } = item;
    const loc = "loc" in item ? item.loc : [];

    // Try to get translated message if this is a TranslatableString
    let errorMessage = msg;
    if (!errorMessage && instanceOfTranslatableString(item)) {
      const translatedMessage = getTranslatedMessage(item, intl, context);
      if (translatedMessage) {
        errorMessage = translatedMessage;
      }
    }

    if (!Array.isArray(loc) || loc.length === 0) {
      errors["error"] = errorMessage;
      return;
    }

    let current = errors;
    for (let i = 0; i < loc.length - 1; i++) {
      const key = String(loc[i]);
      if (!current[key]) current[key] = {};
      current = current[key] as NonNullable<Errors>;
    }

    current[String(loc[loc.length - 1])] = errorMessage;
  });

  return errors;
};

export const getErrorArray = (errors: Errors): string[] => {
  const messages: string[] = [];

  const traverse = (obj: Errors) => {
    for (const key in obj) {
      if (typeof obj[key] === "string") {
        messages.push(obj[key] as string);
      } else {
        traverse(obj[key] as Errors);
      }
    }
  };

  traverse(errors);
  return messages;
};

export const getErrorString = (errors: Errors) => {
  const errorArray = getErrorArray(errors);
  return listToString(errorArray);
};
